/**
 * Caméra 2D : convertit les unités TUILES de la simulation en pixels d'écran
 * (spec §3 — la conversion n'a lieu qu'ici et au rendu).
 */

export interface CameraTarget {
  /** Dimensions du niveau, en tuiles. */
  readonly width: number;
  readonly height: number;
  /** Taille de tuile « native » du niveau, en pixels. */
  readonly tileSize: number;
}

export interface CameraInput {
  /** Dimensions du viewport, en pixels. */
  readonly viewWidth: number;
  readonly viewHeight: number;
  readonly level: CameraTarget;
  /** Point suivi (milieu du duo), en tuiles. */
  readonly focusX: number;
  readonly focusY: number;
}

export interface CameraView {
  /** Échelle appliquée au conteneur monde : pixels par tuile. */
  readonly scale: number;
  /** Translation du conteneur monde, en pixels. */
  readonly x: number;
  readonly y: number;
}

/** Zoom minimum : en dessous, le niveau devient illisible. */
export const MIN_PIXELS_PER_TILE = 10;

/** Zoom maximum, relatif à la taille de tuile native du niveau. */
export const MAX_ZOOM_FACTOR = 2;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Cadre le niveau : on l'affiche entier si le viewport le permet, sinon on
 * suit le duo en restant à l'intérieur des bords du niveau.
 */
export function computeCamera(input: CameraInput): CameraView {
  const { viewWidth, viewHeight, level } = input;
  const fit = Math.min(viewWidth / level.width, viewHeight / level.height);
  const scale = clamp(fit, MIN_PIXELS_PER_TILE, level.tileSize * MAX_ZOOM_FACTOR);

  const worldWidth = level.width * scale;
  const worldHeight = level.height * scale;

  const axis = (view: number, world: number, focus: number): number => {
    if (world <= view) {
      return (view - world) / 2;
    }
    return clamp(view / 2 - focus * scale, view - world, 0);
  };

  return {
    scale,
    x: axis(viewWidth, worldWidth, input.focusX),
    y: axis(viewHeight, worldHeight, input.focusY),
  };
}
